import { InjectionToken, Provider } from '@angular/core';

/**
 * Lo que `core` necesita saber del app que lo usa. Antes cada servicio leía `environment`
 * del app directamente; una lib no puede importar el app (TEC-292), así que el app entrega
 * estos valores al arrancar con `provideCoreConfig()` (ver `apps/app/src/environments/core-config.ts`).
 *
 * Son los mismos campos, con los mismos nombres, que tiene `environment`, más `commitHash`
 * (de `build-info.ts`). No es el TenantConfig de TEC-294: esto se fija en el build.
 */
export interface CoreConfig {
  production: boolean;
  /** Versión del app (`package.json`); viaja en `X-App-Version`. */
  version: string;
  /** Commit del build (`BUILD_INFO.commitHash`); viaja en `X-App-Commit`. */
  commitHash: string;
  /** Base del API, con `/api/v1/` al final. Sólo las peticiones a esta URL llevan token y reintentos. */
  apiBaseUrl: string;
  /** Llave de Preferences donde vive el token de Sanctum. */
  tokenKey: string;
  cryptoKey: string;
  storagePrefix?: string;
  storageEncryption?: boolean;
  /**
   * Servidor WebSocket (protocolo de Pusher; el cliente siempre es pusher-js). TEC-393: con
   * `broadcaster: 'reverb'` el app se conecta al Reverb del propio API (`wss://<host del API><wsPath>`)
   * en vez de a Pusher; sin `broadcaster` (o `'pusher'`) todo queda como antes.
   */
  pusher: {
    key: string;
    cluster: string;
    forceTLS: boolean;
    broadcaster?: 'pusher' | 'reverb';
    /** Sólo Reverb. Sin valor, el host de `apiBaseUrl` (así un preview habla con su propio API). */
    wsHost?: string;
    /** Sólo Reverb. Sin valor, 443 con TLS y 80 sin él. */
    wsPort?: number;
    /** Sólo Reverb: prefijo del proxy, p.ej. `/reverb` (Coolify). */
    wsPath?: string;
  };
}

export const CORE_CONFIG = new InjectionToken<CoreConfig>('CORE_CONFIG');

export function provideCoreConfig(config: CoreConfig): Provider {
  return { provide: CORE_CONFIG, useValue: config };
}
